import { Menu, X } from "lucide-react";
import {
  DARK_BG,
  DARK_BORDER,
  NAV_LINKS,
  WA_LINK,
  type NavigateFn,
  type Page,
} from "../../lib/site";
import { trackCtaClick } from "../../utils/analytics";
import { LogoWordmark } from "./Logo";
import WhatsAppIcon from "../ui/WhatsAppIcon";

export default function Header({
  currentPage,
  navigate,
  mobileMenuOpen,
  setMobileMenuOpen,
}: {
  currentPage: Page | null;
  navigate: NavigateFn;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (v: boolean) => void;
}) {
  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm"
      style={{
        backgroundColor: `${DARK_BG}F5`,
        borderBottom: `1px solid ${DARK_BORDER}`,
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <button type="button" onClick={() => navigate("/")}>
          <LogoWordmark light />
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              type="button"
              key={l.page}
              onClick={() => navigate(l.path)}
              className="text-sm font-medium tracking-wide transition-colors"
              style={{
                color: currentPage === l.page ? "#FF5500" : "rgba(246,243,238,0.65)",
              }}
              onMouseEnter={(e) => {
                if (currentPage !== l.page)
                  (e.target as HTMLElement).style.color = "#F6F3EE";
              }}
              onMouseLeave={(e) => {
                if (currentPage !== l.page)
                  (e.target as HTMLElement).style.color = "rgba(246,243,238,0.65)";
              }}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick("header_whatsapp", WA_LINK)}
            className="flex items-center gap-1.5 text-sm font-medium transition-colors"
            style={{ color: "rgba(246,243,238,0.55)" }}
          >
            <WhatsAppIcon size={16} className="text-[#25D366]" />
            WhatsApp
          </a>
          <button
            type="button"
            onClick={() => {
              trackCtaClick("header_get_quote", "/contact");
              navigate("/contact");
            }}
            className="bg-[#FF5500] text-black text-sm font-bold px-5 py-2 hover:bg-[#E64D00] transition-colors tracking-wide"
          >
            GET A QUOTE
          </button>
        </div>

        <button
          type="button"
          className="md:hidden p-1"
          style={{ color: "#F6F3EE" }}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div
          className="md:hidden px-4 py-3 space-y-1"
          style={{ backgroundColor: DARK_BG, borderTop: `1px solid ${DARK_BORDER}` }}
        >
          {NAV_LINKS.map((l) => (
            <button
              type="button"
              key={l.page}
              onClick={() => navigate(l.path)}
              className="block w-full text-left py-3 text-base font-medium transition-colors"
              style={{
                color: currentPage === l.page ? "#FF5500" : "rgba(246,243,238,0.70)",
                borderBottom: `1px solid ${DARK_BORDER}`,
              }}
            >
              {l.label}
            </button>
          ))}
          <div className="pt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => {
                trackCtaClick("header_mobile_get_quote", "/contact");
                navigate("/contact");
              }}
              className="w-full bg-[#FF5500] text-black font-bold py-3 text-sm tracking-wide"
            >
              GET A QUOTE
            </button>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackCtaClick("header_mobile_whatsapp", WA_LINK)}
              className="w-full font-bold py-3 text-sm text-center tracking-wide flex items-center justify-center gap-2"
              style={{ border: `1px solid rgba(255,255,255,0.18)`, color: "#F6F3EE" }}
            >
              <WhatsAppIcon size={15} className="text-[#25D366]" />
              WHATSAPP US
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
